import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import { ProductCategory } from '../../types';
import { productService } from '../../services/productService';
import './ProductCategoryDeletePage.css';
import { ArrowLeft, Trash2 } from 'lucide-react';

const ProductCategoryDeletePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [category, setCategory] = useState<ProductCategory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadCategory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadCategory = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await productService.getCategoryById(parseInt(id || '0'));
      if (response.data.data) {
        setCategory(response.data.data as ProductCategory);
      }
      setLoading(false);
    } catch (err) {
      setError(t('productCategory.failedToLoadCategory'));
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!category) return;

    setIsDeleting(true);
    try {
      await productService.deleteCategory(category.id);
      navigate('/products/categories', {
        state: { message: t('productCategory.categoryDeletedSuccess') },
      });
    } catch (err) {
      setError(t('productCategory.failedToDeleteCategory'));
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/products/categories/${category?.id}`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="delete-loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!category) {
    return (
      <Layout>
        <div className="delete-container">
          <div className="alert alert-danger">{t('productCategory.categoryNotFound')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="delete-container">
        {/* Navigation Breadcrumb */}
        <nav className="delete-breadcrumb">
          <button
            className="delete-breadcrumb-btn"
            onClick={handleCancel}
            title={t('productCategory.backToCategory')}
          >
            <ArrowLeft size={16} strokeWidth={2} />
            {t('productCategory.backToCategory')}
          </button>
        </nav>

        {/* Page Content */}
        <div className="delete-content">
          {/* Main Delete Card */}
          <div className="delete-card delete-card-main">
            <div className="delete-card-header delete-card-header-warning">
              <Trash2 size={24} strokeWidth={2} className="delete-icon" />
              <h2 className="delete-card-title">{t('productCategory.deleteConfirmationTitle')}</h2>
            </div>

            {/* Error Alert */}
            {error && (
              <div className="alert alert-danger alert-dismissible fade show">
                {error}
              </div>
            )}

            <div className="delete-card-body">
              <div className="delete-warning-section">
                <p className="delete-warning-text">
                  {t('productCategory.deleteConfirmationMessage')}
                </p>
              </div>

              {/* Category Details */}
              <div className="delete-category-info">
                <div className="delete-info-item">
                  <label className="delete-info-label">{t('productCategory.categoryName')}</label>
                  <p className="delete-info-value">{category.name}</p>
                </div>

                <div className="delete-info-item">
                  <label className="delete-info-label">{t('productCategory.categoryId')}</label>
                  <p className="delete-info-value delete-code">{category.id}</p>
                </div>

                {category.description && (
                  <div className="delete-info-item delete-info-item-full">
                    <label className="delete-info-label">{t('productCategory.description')}</label>
                    <p className="delete-info-value">{category.description}</p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="delete-actions">
                <button
                  className="btn-cancel"
                  onClick={handleCancel}
                  disabled={isDeleting}
                  title={t('common.cancel')}
                >
                  {t('common.cancel')}
                </button>

                <button
                  className="btn-delete"
                  onClick={handleDelete}
                  disabled={isDeleting}
                  title={t('common.delete')}
                >
                  {isDeleting ? (
                    <>
                      <span className="btn-spinner"></span>
                      {t('common.delete')}ing...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} strokeWidth={2} />
                      {t('productCategory.deleteCategory')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Info Sidebar */}
          <div className="delete-sidebar">
            {/* Safety Info Card */}
            <div className="delete-card">
              <div className="delete-card-header">
                <h3 className="delete-card-title">{t('productCategory.safetyInformation')}</h3>
              </div>
              <div className="delete-card-body">
                <div className="delete-help-section">
                  <div className="delete-help-item">
                    <strong className="delete-help-strong">{t('productCategory.permanentAction')}</strong>
                    <p>{t('productCategory.deleteWarning')}</p>
                  </div>
                  <div className="delete-help-item">
                    <strong className="delete-help-strong">{t('productCategory.relatedData')}</strong>
                    <p>{t('productCategory.deleteRelatedDataWarning')}</p>
                  </div>
                  <div className="delete-help-item">
                    <strong className="delete-help-strong">{t('productCategory.confirmationRequired')}</strong>
                    <p>{t('productCategory.deleteConfirmationRequired')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProductCategoryDeletePage;
